import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  EmployerLead,
  InteractionLog,
  LeadTask,
  PilotLeadStatus,
  PilotLeadSource,
  PilotLeadPriority,
  EmployeeCountRange,
  InteractionType,
  InteractionChannel,
  LeadTaskStatus,
  LeadTaskType,
} from '@/models/pilot-crm.model';

const PILOT_CRM_LEADS_COLLECTION = 'pilotCrmLeads';
const PILOT_CRM_INTERACTIONS_COLLECTION = 'pilotCrmInteractionLogs';
const PILOT_CRM_TASKS_COLLECTION = 'pilotCrmTasks';

const isValidEmail = (value?: string): boolean => {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const isValidUrl = (value?: string): boolean => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const stripUndefined = <T extends Record<string, unknown>>(input: T): T => {
  const cleaned: Record<string, unknown> = {};
  Object.keys(input).forEach((key) => {
    const value = input[key];
    if (value !== undefined) {
      cleaned[key] = value;
    }
  });
  return cleaned as T;
};

export interface CreateEmployerLeadInput {
  companyName: string;
  industry?: string;
  employeeCountRange?: EmployeeCountRange;
  website?: string;
  hqState?: string;
  contactName?: string;
  contactEmail: string;
  contactRole?: string;
  phone?: string;
  googlePlaceId?: string;
  rating?: number;
  reviewCount?: number;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  placeUrl?: string;
  source: PilotLeadSource;
  status: PilotLeadStatus;
  priority: PilotLeadPriority;
  tags: string[];
  notes?: string;
  nextActionAt?: Date;
  ownerUserId?: string;
}

export interface UpdateEmployerLeadInput extends Partial<CreateEmployerLeadInput> { }

export interface CreateInteractionInput {
  leadId: string;
  type: InteractionType;
  channel: InteractionChannel;
  subject?: string;
  summary: string;
  raw?: string;
}

export interface CreateTaskInput {
  leadId: string;
  title: string;
  type: LeadTaskType;
  dueAt: Date;
  status?: LeadTaskStatus;
  autoExecute?: boolean;
  templateId?: string;
}

export interface UpdateTaskInput {
  title?: string;
  type?: LeadTaskType;
  dueAt?: Date;
  status?: LeadTaskStatus;
  autoExecute?: boolean;
  templateId?: string;
}

export class PilotCrmService {
  static async getLeads(): Promise<EmployerLead[]> {
    const q = query(
      collection(db, PILOT_CRM_LEADS_COLLECTION),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToLead(docSnap.id, docSnap.data()));
  }

  static async getLead(id: string): Promise<EmployerLead> {
    const docRef = doc(db, PILOT_CRM_LEADS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Lead not found');
    }
    return this.docToLead(docSnap.id, docSnap.data());
  }

  static async createLead(input: CreateEmployerLeadInput, userId: string): Promise<string> {
    if (!input.companyName?.trim()) {
      throw new Error('Company name is required');
    }
    if (!isValidEmail(input.contactEmail)) {
      throw new Error('Valid contact email is required');
    }
    if (!isValidUrl(input.website)) {
      throw new Error('Website must be a valid URL');
    }

    const now = Timestamp.now();
    const cleaned = stripUndefined({
      ...input,
      companyName: input.companyName.trim(),
      contactEmail: input.contactEmail.trim().toLowerCase(),
      tags: input.tags || [],
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
      nextActionAt: input.nextActionAt ? Timestamp.fromDate(input.nextActionAt) : undefined,
      googlePlaceId: input.googlePlaceId,
      rating: input.rating,
      reviewCount: input.reviewCount,
      address: input.address?.trim(),
      city: input.city?.trim(),
      state: input.state?.trim(),
      zipCode: input.zipCode?.trim(),
      placeUrl: input.placeUrl?.trim(),
    });

    const docRef = await addDoc(collection(db, PILOT_CRM_LEADS_COLLECTION), cleaned);
    return docRef.id;
  }

  static async updateLead(
    id: string,
    updates: UpdateEmployerLeadInput,
    userId: string
  ): Promise<void> {
    if (updates.companyName !== undefined && !updates.companyName.trim()) {
      throw new Error('Company name cannot be empty');
    }
    if (updates.contactEmail !== undefined && !isValidEmail(updates.contactEmail)) {
      throw new Error('Valid contact email is required');
    }
    if (updates.website !== undefined && !isValidUrl(updates.website)) {
      throw new Error('Website must be a valid URL');
    }

    const cleaned = stripUndefined({
      ...updates,
      companyName: updates.companyName?.trim(),
      contactEmail: updates.contactEmail?.trim().toLowerCase(),
      updatedAt: Timestamp.now(),
      updatedBy: userId,
      nextActionAt: updates.nextActionAt ? Timestamp.fromDate(updates.nextActionAt) : undefined,
      googlePlaceId: updates.googlePlaceId,
      rating: updates.rating,
      reviewCount: updates.reviewCount,
      address: updates.address?.trim(),
      city: updates.city?.trim(),
      state: updates.state?.trim(),
      zipCode: updates.zipCode?.trim(),
      placeUrl: updates.placeUrl?.trim(),
    });

    await updateDoc(doc(db, PILOT_CRM_LEADS_COLLECTION, id), cleaned);
  }

  static async bulkUpdateLeads(
    leadIds: string[],
    updates: Pick<UpdateEmployerLeadInput, 'status' | 'ownerUserId' | 'priority'>,
    userId: string
  ): Promise<void> {
    if (leadIds.length === 0) {
      return;
    }

    const cleaned = stripUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });

    const batch = writeBatch(db);
    leadIds.forEach((leadId) => {
      batch.update(doc(db, PILOT_CRM_LEADS_COLLECTION, leadId), cleaned);
    });
    await batch.commit();
  }

  static async getInteractions(leadId: string): Promise<InteractionLog[]> {
    const q = query(
      collection(db, PILOT_CRM_INTERACTIONS_COLLECTION),
      where('leadId', '==', leadId)
    );
    const snapshot = await getDocs(q);
    const interactions = snapshot.docs.map((docSnap) =>
      this.docToInteraction(docSnap.id, docSnap.data())
    );
    return interactions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  static async createInteraction(input: CreateInteractionInput, userId: string): Promise<string> {
    if (!input.summary?.trim()) {
      throw new Error('Interaction summary is required');
    }

    const now = Timestamp.now();
    const interactionData = stripUndefined({
      leadId: input.leadId,
      type: input.type,
      channel: input.channel,
      subject: input.subject?.trim(),
      summary: input.summary.trim(),
      raw: input.raw?.trim(),
      createdAt: now,
      createdBy: userId,
    });

    const batch = writeBatch(db);
    const interactionRef = doc(collection(db, PILOT_CRM_INTERACTIONS_COLLECTION));
    batch.set(interactionRef, interactionData);
    batch.update(doc(db, PILOT_CRM_LEADS_COLLECTION, input.leadId), {
      updatedAt: now,
      updatedBy: userId,
    });
    await batch.commit();
    return interactionRef.id;
  }

  static async getTasks(leadId?: string): Promise<LeadTask[]> {
    const baseQuery = leadId
      ? query(
        collection(db, PILOT_CRM_TASKS_COLLECTION),
        where('leadId', '==', leadId)
      )
      : query(collection(db, PILOT_CRM_TASKS_COLLECTION));

    const snapshot = await getDocs(baseQuery);
    const tasks = snapshot.docs.map((docSnap) => this.docToTask(docSnap.id, docSnap.data()));
    return tasks.sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
  }

  static async createTask(input: CreateTaskInput, userId: string): Promise<string> {
    if (!input.title?.trim()) {
      throw new Error('Task title is required');
    }
    if (!input.dueAt) {
      throw new Error('Task due date is required');
    }

    const now = Timestamp.now();
    const taskData = stripUndefined({
      leadId: input.leadId,
      title: input.title.trim(),
      type: input.type,
      dueAt: Timestamp.fromDate(input.dueAt),
      status: input.status || 'open',
      autoExecute: input.autoExecute || false,
      templateId: input.templateId || undefined,
      executionStatus: input.autoExecute ? 'pending' : undefined,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });

    const batch = writeBatch(db);
    const taskRef = doc(collection(db, PILOT_CRM_TASKS_COLLECTION));
    batch.set(taskRef, taskData);
    batch.update(doc(db, PILOT_CRM_LEADS_COLLECTION, input.leadId), {
      updatedAt: now,
      updatedBy: userId,
    });
    await batch.commit();
    return taskRef.id;
  }

  static async updateTask(taskId: string, updates: UpdateTaskInput, userId: string): Promise<void> {
    if (updates.title !== undefined && !updates.title.trim()) {
      throw new Error('Task title cannot be empty');
    }

    const cleaned = stripUndefined({
      ...updates,
      title: updates.title?.trim(),
      dueAt: updates.dueAt ? Timestamp.fromDate(updates.dueAt) : undefined,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
      autoExecute: updates.autoExecute,
      templateId: updates.templateId,
      // Reset execution status if autoExecute is toggled on
      executionStatus: updates.autoExecute === true ? 'pending' : (updates.autoExecute === false ? null : undefined),
    });

    await updateDoc(doc(db, PILOT_CRM_TASKS_COLLECTION, taskId), cleaned);
  }

  private static docToLead(id: string, data: any): EmployerLead {
    return {
      id,
      companyName: data.companyName || '',
      industry: data.industry || undefined,
      employeeCountRange: data.employeeCountRange || undefined,
      website: data.website || undefined,
      hqState: data.hqState || undefined,
      contactName: data.contactName || undefined,
      contactEmail: data.contactEmail || '',
      contactRole: data.contactRole || undefined,
      phone: data.phone || undefined,
      googlePlaceId: data.googlePlaceId || undefined,
      rating: data.rating || undefined,
      reviewCount: data.reviewCount || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      zipCode: data.zipCode || undefined,
      placeUrl: data.placeUrl || undefined,
      source: data.source || 'other',
      status: data.status || 'new',
      priority: data.priority || 'normal',
      tags: data.tags || [],
      notes: data.notes || undefined,
      nextActionAt: data.nextActionAt?.toDate(),
      ownerUserId: data.ownerUserId || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
      updatedBy: data.updatedBy || 'system',
    };
  }

  private static docToInteraction(id: string, data: any): InteractionLog {
    return {
      id,
      leadId: data.leadId,
      type: data.type || 'note',
      channel: data.channel || 'in_app',
      subject: data.subject || undefined,
      summary: data.summary || '',
      raw: data.raw || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
    };
  }

  private static docToTask(id: string, data: any): LeadTask {
    return {
      id,
      leadId: data.leadId,
      title: data.title || '',
      type: data.type || 'other',
      dueAt: data.dueAt?.toDate() || new Date(),
      status: data.status || 'open',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
      updatedBy: data.updatedBy || 'system',
    };
  }
}
